import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { Music } from 'lucide-react';
import { Micro } from '@/components/atoms/micro';

export type AddRepertoireHeaderBandProps = {
  pieceId: string;
  pieceTitle: string;
  composerName: string | null;
  formNames: readonly string[];
};

const TRADITIONAL = 'Traditional';

function buildSubtitle(composerName: string | null, formNames: readonly string[]): string {
  const parts: string[] = [];
  parts.push(composerName ?? TRADITIONAL);
  for (const form of formNames) parts.push(form);
  return parts.join(' · ');
}

export function AddRepertoireHeaderBand({
  pieceId,
  pieceTitle,
  composerName,
  formNames,
}: AddRepertoireHeaderBandProps): ReactElement {
  const subtitle = buildSubtitle(composerName, formNames);
  return (
    <header className="flex items-start gap-4">
      <div
        aria-hidden
        className="flex h-14 w-14 flex-none items-center justify-center rounded-[var(--r-md)] bg-[var(--brand-wash)] text-[var(--brand)]"
      >
        <Music className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <Micro>You&apos;re adding</Micro>
        <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-[var(--ink)] md:text-3xl">
          {pieceTitle}
        </h2>
        <p className="mt-1 text-sm text-[var(--ink-3)]">{subtitle}</p>
      </div>
      <Link
        to="/library/$pieceId"
        params={{ pieceId }}
        className="inline-flex h-10 flex-none items-center gap-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] px-3 text-xs font-bold text-[var(--ink)] transition-colors hover:border-[var(--brand)]"
      >
        Open in library
      </Link>
    </header>
  );
}
